import { Link } from "react-router-dom";
import { X, Plus, Minus, ShoppingBag, Trash2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { spiceLabel } from "./VariantPicker";

export default function CartDrawer() {
  const { items, open, setOpen, updateQty, remove, subtotal, count, clear } = useCart();

  return (
    <>
      <div
        data-testid="cart-backdrop"
        onClick={() => setOpen(false)}
        className={`fixed inset-0 bg-[#0E0E1A]/40 backdrop-blur-md z-[60] transition-opacity ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <aside
        data-testid="cart-drawer"
        className={`glass-solid glass-shine !fixed top-3 right-3 bottom-3 w-[calc(100%-1.5rem)] sm:w-[420px] z-[70] rounded-3xl transition-transform duration-500 flex flex-col overflow-hidden ${
          open ? "translate-x-0" : "translate-x-[110%]"
        }`}
      >
        <header className="flex items-center justify-between p-5 border-b border-white/40">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-[#C9853E]" />
            <span className="font-display text-xl font-bold text-[#0E0E1A]">Your Cart</span>
            <span className="ml-1 text-sm text-[#57534E]">
              ({count} {count === 1 ? "item" : "items"})
            </span>
          </div>
          <button
            data-testid="cart-close-btn"
            onClick={() => setOpen(false)}
            className="p-2 text-[#57534E] hover:text-[#0E0E1A]"
            aria-label="Close cart"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="cart-empty">
              <ShoppingBag className="w-12 h-12 text-[#C9853E]/30 mb-4" />
              <div className="font-display text-xl font-bold text-[#0E0E1A]">
                Your cart is empty
              </div>
              <p className="text-[#57534E] text-sm mt-2 max-w-xs">
                Add a butter chicken pizza, a curry, some naan… you know how this goes.
              </p>
              <Link
                to="/menu"
                onClick={() => setOpen(false)}
                data-testid="cart-empty-menu-link"
                className="mt-6 inline-flex items-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] text-white rounded-full px-6 py-3 font-bold text-sm"
              >
                Browse the menu
              </Link>
            </div>
          ) : (
            <ul className="divide-y divide-[#E7E5E4]" data-testid="cart-items">
              {items.map((it, i) => (
                <li key={`${it.item_id}-${it.variant_name || ""}-${i}`} className="py-4 flex gap-4" data-testid={`cart-item-${i}`}>
                  <div className="flex-1">
                    <div className="font-display font-bold text-[#0E0E1A] leading-snug">{it.name}</div>
                    {it.variant_label && (
                      <div className="text-xs text-[#57534E] mt-0.5">{it.variant_label}</div>
                    )}
                    {it.spice_level && (
                      <div className="text-[10px] text-[#C9853E] font-bold uppercase tracking-wider mt-0.5 inline-flex items-center gap-1">
                        🌶 {spiceLabel(it.spice_level)}
                      </div>
                    )}
                    {it.dip && (
                      <div className="text-[10px] text-[#4A6741] font-bold uppercase tracking-wider mt-0.5 block">
                        🥣 {it.dip} · free
                      </div>
                    )}
                    <div className="flex items-center gap-2 mt-3">
                      <button
                        data-testid={`cart-decrease-${i}`}
                        onClick={() => updateQty(it, it.quantity - 1)}
                        className="w-7 h-7 rounded-full border border-[#E7E5E4] flex items-center justify-center hover:bg-white"
                        aria-label="Decrease quantity"
                      >
                        <Minus className="w-3 h-3" />
                      </button>
                      <span className="text-sm font-bold w-6 text-center" data-testid={`cart-qty-${i}`}>
                        {it.quantity}
                      </span>
                      <button
                        data-testid={`cart-increase-${i}`}
                        onClick={() => updateQty(it, it.quantity + 1)}
                        className="w-7 h-7 rounded-full border border-[#E7E5E4] flex items-center justify-center hover:bg-white"
                        aria-label="Increase quantity"
                      >
                        <Plus className="w-3 h-3" />
                      </button>
                      <button
                        data-testid={`cart-remove-${i}`}
                        onClick={() => remove(it)}
                        className="ml-auto text-[#57534E] hover:text-[#C9853E] p-1"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display font-bold text-[#C9853E]">
                      ${(it.price * it.quantity).toFixed(2)}
                    </div>
                    <div className="text-xs text-[#57534E] mt-0.5">${it.price.toFixed(2)} ea</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {items.length > 0 && (
          <footer className="border-t border-white/40 p-5 bg-white/40 backdrop-blur-xl">
            <div className="flex items-baseline justify-between mb-4">
              <span className="text-sm text-[#57534E]">Subtotal</span>
              <span className="font-display text-2xl font-extrabold text-[#0E0E1A]" data-testid="cart-subtotal">
                ${subtotal.toFixed(2)}
              </span>
            </div>
            <div className="text-xs text-[#57534E] mb-4">Taxes calculated at checkout. Pickup only.</div>
            <Link
              to="/order"
              onClick={() => setOpen(false)}
              data-testid="cart-checkout-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C9853E] hover:bg-[#A6692D] text-white rounded-full px-6 py-4 font-bold transition-all shadow-[0_8px_20px_-8px_rgba(201,133,62,0.6)]"
            >
              Continue to checkout
            </Link>
            <button
              data-testid="cart-clear-btn"
              onClick={clear}
              className="w-full text-center text-xs text-[#57534E] hover:text-[#C9853E] mt-3"
            >
              Clear cart
            </button>
          </footer>
        )}
      </aside>
    </>
  );
}
